import React, { useEffect, useState } from "react";
import { Modal, Platform, Pressable, ScrollView, StyleSheet, View } from "react-native";
import { ArrowLeftIcon, CalendarBlankIcon, FileIcon, XIcon } from "@phosphor-icons/react";
import { DatePickerModal } from "react-native-paper-dates";

import CustomButton from "../../../../../components/common/general/CustomButton/CustomButton";
import CustomSwitch from "../../../../../components/common/general/CustomSwitch/CustomSwitch";
import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import CustomTextInput from "../../../../../components/common/general/CustomTextInput/CustomTextInput";
import CustomRichTextEditor from "../../../../../components/TextEditor/CustomRichTextEditor";
import FileUploadComponent from "../../../../../components/file/FileUploadComponent/FileUploadComponent";
import type { ProcessedFile } from "../../../../../hooks/useFileUpload";
import { StoragePathType } from "@axio/graphql/generated/graphql";
import { colors } from "../../../../../styles/design-system";

type ModernizerAssignmentFile = {
  id: string;
  fileName: string;
  fileURL: string;
};

type ModernizerAddAssignmentInput = {
  title: string;
  descriptionHtml?: string;
  dueDate?: Date;
  videoUrl?: string;
  enableFileUpload: boolean;
  files: ModernizerAssignmentFile[];
};

interface ModernizerAddAssignmentSheetProps {
  open: boolean;
  moduleTitle: string;
  isSubmitting: boolean;
  onClose: () => void;
  onSave: (input: ModernizerAddAssignmentInput) => Promise<void>;
}

const isValidHttpUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

function ModernizerAddAssignmentSheet({
  open,
  moduleTitle,
  isSubmitting,
  onClose,
  onSave,
}: ModernizerAddAssignmentSheetProps) {
  const [title, setTitle] = useState("");
  const [dueDate, setDueDate] = useState<Date | undefined>();
  const [descriptionHtml, setDescriptionHtml] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [enableFileUpload, setEnableFileUpload] = useState(true);
  const [files, setFiles] = useState<ModernizerAssignmentFile[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isDueDatePickerOpen, setIsDueDatePickerOpen] = useState(false);

  useEffect(() => {
    if (!open) return;

    setTitle("");
    setDueDate(undefined);
    setDescriptionHtml("");
    setVideoUrl("");
    setEnableFileUpload(true);
    setFiles([]);
    setFormError(null);
    setUploadError(null);
    setIsDueDatePickerOpen(false);
  }, [open]);

  const handleFilesUploaded = (processedFiles: ProcessedFile[]) => {
    setFiles((currentFiles) => [
      ...currentFiles,
      ...processedFiles.map(({ file }) => ({
        id: file.id,
        fileName: file.fileName,
        fileURL: file.fileURL,
      })),
    ]);
    setUploadError(null);
  };

  const handleSubmit = async () => {
    const trimmedTitle = title.trim();
    const trimmedVideoUrl = videoUrl.trim();

    if (!trimmedTitle) {
      setFormError("Assignment name is required.");
      return;
    }

    if (trimmedVideoUrl && !isValidHttpUrl(trimmedVideoUrl)) {
      setFormError("Enter a valid video URL.");
      return;
    }

    setFormError(null);
    await onSave({
      title: trimmedTitle,
      descriptionHtml: descriptionHtml.trim() || undefined,
      dueDate,
      videoUrl: trimmedVideoUrl || undefined,
      enableFileUpload,
      files,
    });
  };

  return (
    <Modal visible={open} transparent animationType={Platform.OS === "web" ? "none" : "slide"} onRequestClose={onClose}>
      <View style={styles.overlay}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Close assignment editor"
          style={styles.backdrop}
          onPress={onClose}
        />
        <View
          style={styles.sheet}
          role="dialog"
          accessibilityRole="dialog"
          accessibilityLabel={`Create assignment in ${moduleTitle}`}>
          <View style={styles.header}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Back to course editor"
              onPress={onClose}
              style={styles.headerButton}>
              <ArrowLeftIcon size={16} color={colors.grey[600]} />
            </Pressable>
            <CustomText
              text="Create Assignment"
              useTranslationText={false}
              size="s"
              weight="bold"
              style={styles.headerTitle}
            />
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Close assignment editor"
              onPress={onClose}
              style={styles.headerButton}>
              <XIcon size={16} color={colors.grey[500]} />
            </Pressable>
          </View>

          <ScrollView
            style={styles.body}
            contentContainerStyle={styles.bodyContent}
            showsVerticalScrollIndicator
            tabIndex={0}>
            <SectionLabel text="Basics" />
            <FieldLabel text="Assignment Name" />
            <CustomTextInput
              accessibilityLabel="Assignment name"
              value={title}
              onChangeText={setTitle}
              placeholder="Enter assignment name"
              editable={!isSubmitting}
              error={formError === "Assignment name is required." ? formError : undefined}
            />

            <FieldLabel text="Due Date" />
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Assignment due date"
              onPress={() => setIsDueDatePickerOpen(true)}
              disabled={isSubmitting}
              style={({ pressed }) => [
                styles.dateInput,
                pressed && !isSubmitting ? styles.dateInputPressed : undefined,
                isSubmitting ? styles.dateInputDisabled : undefined,
              ]}>
              <CalendarBlankIcon size={16} color={colors.grey[500]} />
              <CustomText
                text={dueDate ? dueDate.toLocaleDateString("en-US") : "Select due date"}
                useTranslationText={false}
                size="xs"
                style={dueDate ? styles.dateInputValue : styles.dateInputPlaceholder}
              />
            </Pressable>
            <DatePickerModal
              locale="en"
              mode="single"
              visible={isDueDatePickerOpen}
              date={dueDate}
              onDismiss={() => setIsDueDatePickerOpen(false)}
              onConfirm={({ date }) => {
                setDueDate(date);
                setIsDueDatePickerOpen(false);
              }}
              animationType={Platform.OS === "web" ? "none" : "slide"}
            />

            <FieldLabel text="Instructions" />
            <View style={styles.editor}>
              <CustomRichTextEditor
                value={descriptionHtml}
                onChange={setDescriptionHtml}
                isSubmitting={isSubmitting}
                placeholder="Enter assignment instructions..."
                activelyEditing
                restrictImageUpload
              />
            </View>

            <View style={styles.divider} />
            <SectionLabel text="Content" />
            <FieldLabel text="Files" />
            <CustomText
              text="Upload assignment materials for students"
              useTranslationText={false}
              size="xs"
              style={styles.helperText}
            />
            <FileUploadComponent
              storagePath={StoragePathType.Resources}
              afterUpload={handleFilesUploaded}
              onError={(message) => setUploadError(message ?? "File upload failed.")}
              multiple
            />
            {uploadError ? (
              <CustomText text={uploadError} useTranslationText={false} size="xs" style={styles.errorText} />
            ) : null}
            {files.map((file) => (
              <View key={file.id} style={styles.fileRow}>
                <FileIcon size={16} color={colors.grey[600]} />
                <CustomText
                  text={file.fileName}
                  useTranslationText={false}
                  size="xs"
                  style={styles.fileName}
                  numberOfLines={1}
                />
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={`Remove ${file.fileName}`}
                  onPress={() => setFiles((currentFiles) => currentFiles.filter(({ id }) => id !== file.id))}
                  disabled={isSubmitting}>
                  <XIcon size={14} color={colors.grey[500]} />
                </Pressable>
              </View>
            ))}

            <FieldLabel text="Video Link" />
            <CustomText
              text="Paste a YouTube or Vimeo link here"
              useTranslationText={false}
              size="xs"
              style={styles.helperText}
            />
            <View style={styles.videoInputField}>
              <CustomTextInput
                accessibilityLabel="Video link"
                value={videoUrl}
                onChangeText={(value) => {
                  setVideoUrl(value);
                  if (formError === "Enter a valid video URL.") {
                    setFormError(null);
                  }
                }}
                placeholder="Paste a YouTube or Vimeo link..."
                editable={!isSubmitting}
                autoCapitalize="none"
                error={formError === "Enter a valid video URL." ? formError : undefined}
                showErrorMessage={false}
              />
              {formError === "Enter a valid video URL." ? (
                <CustomText text={formError} useTranslationText={false} size="xs" style={styles.errorText} />
              ) : null}
            </View>

            <View style={styles.divider} />
            <SectionLabel text="Submission" />
            <View style={styles.switchRow}>
              <View style={styles.switchCopy}>
                <CustomText
                  text="Enable Student File Upload"
                  useTranslationText={false}
                  size="xs"
                  weight="medium"
                  style={styles.switchTitle}
                />
                <CustomText
                  text="Allow students to upload files as part of their submission"
                  useTranslationText={false}
                  size="xs"
                  style={styles.helperText}
                />
              </View>
              <CustomSwitch
                value={enableFileUpload}
                onValueChange={setEnableFileUpload}
                disabled={isSubmitting}
                size="s"
                accessibilityLabel="Enable student file upload"
              />
            </View>

            {formError && formError !== "Assignment name is required." && formError !== "Enter a valid video URL." ? (
              <CustomText text={formError} useTranslationText={false} size="xs" style={styles.errorText} />
            ) : null}
          </ScrollView>

          <View style={styles.footer}>
            <CustomButton
              text="Cancel"
              size="s"
              styleType="primaryTransparentStroke"
              onPress={onClose}
              disabled={isSubmitting}
            />
            <CustomButton
              text="Create Assignment"
              accessibilityLabel="Create assignment"
              size="s"
              styleType="primary"
              onPress={handleSubmit}
              disabled={isSubmitting || !title.trim()}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
}

function SectionLabel({ text }: { text: string }) {
  return (
    <CustomText
      text={text.toUpperCase()}
      useTranslationText={false}
      size="xs"
      weight="bold"
      style={styles.sectionLabel}
    />
  );
}

function FieldLabel({ text }: { text: string }) {
  return <CustomText text={text} useTranslationText={false} size="xs" weight="medium" style={styles.fieldLabel} />;
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    flexDirection: "row",
    justifyContent: "flex-end",
    backgroundColor: "rgba(16, 24, 40, 0.24)",
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  sheet: {
    width: 640,
    maxWidth: "100%",
    height: "100%",
    backgroundColor: colors.white,
    borderLeftWidth: 1,
    borderLeftColor: colors.grey[200],
    shadowColor: "#101828",
    shadowOffset: { width: -8, height: 0 },
    shadowOpacity: 0.12,
    shadowRadius: 24,
    elevation: 16,
  },
  header: {
    height: 56,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    flexDirection: "row",
    alignItems: "center",
  },
  headerButton: {
    width: 28,
    height: 28,
    alignItems: "center",
    justifyContent: "center",
  },
  headerTitle: {
    flex: 1,
    color: colors.grey[900],
    marginLeft: 4,
  },
  body: {
    flex: 1,
  },
  bodyContent: {
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 20,
    gap: 8,
  },
  sectionLabel: {
    color: colors.grey[500],
    fontSize: 10,
    letterSpacing: 0.6,
    marginBottom: 2,
  },
  fieldLabel: {
    color: colors.grey[700],
    marginTop: 4,
  },
  helperText: {
    color: colors.grey[500],
    lineHeight: 16,
    marginTop: -4,
  },
  dateInput: {
    minHeight: 36,
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[300],
    borderRadius: 8,
    paddingHorizontal: 12,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  dateInputPressed: {
    borderColor: colors.brand[500],
  },
  dateInputDisabled: {
    backgroundColor: colors.grey[50],
    opacity: 0.6,
  },
  dateInputPlaceholder: {
    color: colors.grey[500],
  },
  dateInputValue: {
    color: colors.grey[700],
  },
  editor: {
    minHeight: 112,
  },
  divider: {
    height: 1,
    backgroundColor: colors.grey[200],
    marginVertical: 12,
  },
  fileRow: {
    minHeight: 36,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 6,
    paddingHorizontal: 10,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  fileName: {
    flex: 1,
    color: colors.grey[700],
  },
  switchRow: {
    minHeight: 58,
    borderRadius: 8,
    backgroundColor: colors.grey[50],
    paddingHorizontal: 12,
    paddingVertical: 10,
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  switchCopy: {
    flex: 1,
    gap: 4,
  },
  switchTitle: {
    color: colors.grey[800],
  },
  errorText: {
    color: colors.error[600],
  },
  videoInputField: {
    gap: 6,
  },
  footer: {
    minHeight: 44,
    paddingHorizontal: 20,
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
});

export default ModernizerAddAssignmentSheet;
export type { ModernizerAddAssignmentInput };
